/**
 * GSAP Scroll Animations
 *
 * Production-ready scroll animation system using GSAP ScrollTrigger.
 * Replaces AOS with better performance and GPU acceleration.
 *
 * 📖 Full documentation: See scroll-animations.md in this directory
 *
 * QUICK START:
 * <div data-animate="fade-up">Fades up on scroll</div>
 * <div data-animate="fade-up" data-reset="true">Resets when scrolling back</div>
 * <div data-animate="fade-up" data-scrub="true">Tied to scroll position</div>
 * <h1 data-animate="fade-up" data-split="chars">Animates by character</h1>
 * <h1 data-animate="scramble">Scrambles into view</h1>
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// Try to import optional GSAP plugins (they may not be available)
let SplitText = null;
let ScrambleTextPlugin = null;

try {
	// These are premium/bonus plugins - only import if available
	SplitText = require('gsap/SplitText').SplitText;
	gsap.registerPlugin(SplitText);
} catch (e) {
	// SplitText not available
}

try {
	ScrambleTextPlugin = require('gsap/ScrambleTextPlugin').ScrambleTextPlugin;
	gsap.registerPlugin(ScrambleTextPlugin);
} catch (e) {
	// ScrambleTextPlugin not available
}

class ScrollAnimations {
	constructor() {
		this.animations = new Map();
		this.defaults = {
			duration: 0.8,
			delay: 0,
			ease: 'power2.out',
			start: 'top 85%',
			end: 'bottom center',
			once: true,
			reset: true,
			scrub: false,
			distance: 60,
			stagger: 0.03,
			split: null,
			scrambleChars: 'X-_\\/<>',
			scrambleSpeed: 0.3,
		};

		// Check plugin availability
		this.hasSplitText = SplitText !== null;
		this.hasScrambleText = ScrambleTextPlugin !== null;

		if (!this.hasSplitText) {
			console.warn('[ScrollAnimations] SplitText plugin not available. data-split will be ignored.');
		}
		if (!this.hasScrambleText) {
			console.warn('[ScrollAnimations] ScrambleTextPlugin not available. scramble animations will be ignored.');
		}

		this.setupAnimationTypes();
	}

	/**
	 * Helper to get attributes with proper type conversion
	 * Supports both data-animate-* and data-aos-* attributes
	 */
	getAttr(el, name, fallback) {
		// Try new format first (data-animate-duration)
		let val = el.getAttribute(`data-${name}`);

		// Fallback to AOS format (data-aos-duration) for backward compatibility
		if (!val && name !== 'animate') {
			val = el.getAttribute(`data-aos-${name}`);
			// Convert AOS milliseconds to seconds
			if (val && ( name === 'duration' || name === 'delay' )) {
				val = parseFloat(val) / 1000;
			}
		}

		if (!val) return fallback;

		// Handle boolean values
		if (val === 'true') return true;
		if (val === 'false') return false;

		// Handle numeric values
		if (!isNaN(val)) return parseFloat(val);

		return val;
	}

	/**
	 * Parse animation config from element attributes
	 */
	getConfig(el) {
		// Support both data-animate and data-aos
		const type = el.dataset.animate || el.dataset.aos;

		// Get scrub value - can be boolean or number
		let scrub = this.getAttr(el, 'scrub', this.defaults.scrub);
		if (scrub === 'true') scrub = true;
		if (scrub === 'false') scrub = false;

		// If reset is true, override once
		const reset = this.getAttr(el, 'reset', this.defaults.reset);
		const once = reset ? false : this.getAttr(el, 'once', this.defaults.once);

		return {
			type: type,
			duration: this.getAttr(el, 'duration', this.defaults.duration),
			delay: this.getAttr(el, 'delay', this.defaults.delay),
			ease: this.getAttr(el, 'ease', this.defaults.ease),
			start: this.getAttr(el, 'start', this.defaults.start),
			end: this.getAttr(el, 'end', this.defaults.end),
			once: once,
			reset: reset,
			scrub: scrub,
			distance: this.getAttr(el, 'distance', this.defaults.distance),
			stagger: this.getAttr(el, 'stagger', this.defaults.stagger),
			split: this.getAttr(el, 'split', this.defaults.split),
			scrambleChars: this.getAttr(el, 'scramble-chars', this.defaults.scrambleChars),
			scrambleSpeed: this.getAttr(el, 'scramble-speed', this.defaults.scrambleSpeed),
		};
	}

	/**
	 * Create animation with GPU optimization
	 * Supports scrub, reset, and split text
	 */
	createAnimation(el, config, fromProps, toProps = {}) {
		// Add GPU acceleration properties
		const gpuProps = {
			force3D: true,
			backfaceVisibility: 'hidden',
			perspective: 1000,
			willChange: 'transform, opacity',
		};

		// Determine target elements (original element or split text)
		let targets = el;
		let splitInstance = null;

		// Apply SplitText if requested
		if (config.split && this.hasSplitText) {
			try {
				splitInstance = new SplitText(el, { type: config.split });
				// Animate the split elements
				if (config.split === 'chars') targets = splitInstance.chars;
				else if (config.split === 'words') targets = splitInstance.words;
				else if (config.split === 'lines') targets = splitInstance.lines;

				// Store split instance for cleanup
				el._splitInstance = splitInstance;
			} catch (e) {
				console.warn('[ScrollAnimations] SplitText failed:', e);
				targets = el;
			}
		}

		// Set initial state
		gsap.set(targets, {
			...fromProps,
			...gpuProps,
		});

		// Prepare animation properties
		const animProps = {
			...toProps,
			...gpuProps,
			willChange: 'auto', // Remove after animation
			delay: config.delay,
		};

		// Prepare ScrollTrigger configuration
		const scrollTriggerConfig = {
			trigger: el,
			start: config.start,
			invalidateOnRefresh: true,
			// markers: true, // Uncomment for debugging
		};

		// Handle scrub vs standard animation
		if (config.scrub !== false) {
			// SCRUB MODE: Animation tied to scroll position
			animProps.ease = 'none'; // Scrub animations should use linear ease
			scrollTriggerConfig.end = config.end;
			scrollTriggerConfig.scrub = config.scrub === true ? true : config.scrub;
		} else {
			// STANDARD MODE: Animation plays on trigger
			animProps.duration = config.duration;
			animProps.ease = config.ease;

			// Determine toggleActions based on reset setting
			if (config.reset) {
				// Reset animation when scrolling back up
				scrollTriggerConfig.toggleActions = 'play reverse play reverse';
			} else if (config.once) {
				// Play once, don't reset
				scrollTriggerConfig.once = true;
				scrollTriggerConfig.toggleActions = 'play none none none';
			} else {
				// Play every time entering viewport
				scrollTriggerConfig.toggleActions = 'play none none reset';
			}
		}

		// Add stagger for split text
		if (config.split && targets !== el) {
			animProps.stagger = config.stagger;
		}

		// Create animation
		gsap.to(targets, {
			...animProps,
			scrollTrigger: scrollTriggerConfig,
		});
	}

	/**
	 * Setup all animation types
	 */
	setupAnimationTypes() {
		// Basic fade animations
		this.registerAnimation('fade', (el, config) => {
			this.createAnimation(el, config,
					{ opacity: 0 },
					{ opacity: 1 },
			);
		});

		this.registerAnimation('fade-in', (el, config) => {
			this.createAnimation(el, config,
					{ opacity: 0 },
					{ opacity: 1 },
			);
		});

		this.registerAnimation('fade-up', (el, config) => {
			this.createAnimation(el, config,
					{ opacity: 0, y: config.distance },
					{ opacity: 1, y: 0 },
			);
		});

		this.registerAnimation('fade-down', (el, config) => {
			this.createAnimation(el, config,
					{ opacity: 0, y: -config.distance },
					{ opacity: 1, y: 0 },
			);
		});

		this.registerAnimation('fade-left', (el, config) => {
			this.createAnimation(el, config,
					{ opacity: 0, x: config.distance },
					{ opacity: 1, x: 0 },
			);
		});

		this.registerAnimation('fade-right', (el, config) => {
			this.createAnimation(el, config,
					{ opacity: 0, x: -config.distance },
					{ opacity: 1, x: 0 },
			);
		});

		// Scale animations
		this.registerAnimation('zoom-in', (el, config) => {
			this.createAnimation(el, config,
					{ opacity: 0, scale: 0.8 },
					{ opacity: 1, scale: 1 },
			);
		});

		this.registerAnimation('zoom-out', (el, config) => {
			this.createAnimation(el, config,
					{ opacity: 0, scale: 1.2 },
					{ opacity: 1, scale: 1 },
			);
		});

		// Slide animations (no fade)
		this.registerAnimation('slide-up', (el, config) => {
			this.createAnimation(el, config,
					{ y: config.distance },
					{ y: 0 },
			);
		});

		this.registerAnimation('slide-down', (el, config) => {
			this.createAnimation(el, config,
					{ y: -config.distance },
					{ y: 0 },
			);
		});

		this.registerAnimation('slide-left', (el, config) => {
			this.createAnimation(el, config,
					{ x: config.distance },
					{ x: 0 },
			);
		});

		this.registerAnimation('slide-right', (el, config) => {
			this.createAnimation(el, config,
					{ x: -config.distance },
					{ x: 0 },
			);
		});

		// Rotation animations
		this.registerAnimation('flip-up', (el, config) => {
			this.createAnimation(el, config,
					{ opacity: 0, rotationX: -90, transformPerspective: 1000 },
					{ opacity: 1, rotationX: 0 },
			);
		});

		this.registerAnimation('flip-down', (el, config) => {
			this.createAnimation(el, config,
					{ opacity: 0, rotationX: 90, transformPerspective: 1000 },
					{ opacity: 1, rotationX: 0 },
			);
		});

		this.registerAnimation('flip-left', (el, config) => {
			this.createAnimation(el, config,
					{ opacity: 0, rotationY: 90, transformPerspective: 1000 },
					{ opacity: 1, rotationY: 0 },
			);
		});

		this.registerAnimation('flip-right', (el, config) => {
			this.createAnimation(el, config,
					{ opacity: 0, rotationY: -90, transformPerspective: 1000 },
					{ opacity: 1, rotationY: 0 },
			);
		});

		// Scramble text animation (requires ScrambleTextPlugin)
		this.registerAnimation('scramble', (el, config) => {
			if (!this.hasScrambleText) {
				console.warn('[ScrollAnimations] scramble animation requires ScrambleTextPlugin');
				// Fallback to fade-in
				this.createAnimation(el, config,
						{ opacity: 0 },
						{ opacity: 1 },
				);
				return;
			}

			// Store original text before any modifications
			const originalText = el.textContent.trim();

			// Prepare ScrollTrigger configuration
			const scrollTriggerConfig = {
				trigger: el,
				start: config.start,
				invalidateOnRefresh: true,
			};

			// Handle scrub vs standard animation
			if (config.scrub !== false) {
				scrollTriggerConfig.end = config.end;
				scrollTriggerConfig.scrub = config.scrub === true ? true : config.scrub;
			} else {
				if (config.reset) {
					scrollTriggerConfig.toggleActions = 'play reverse play reverse';
				} else if (config.once) {
					scrollTriggerConfig.once = true;
					scrollTriggerConfig.toggleActions = 'play none none none';
				} else {
					scrollTriggerConfig.toggleActions = 'play none none reset';
				}
			}

			// Set initial scrambled state
			const scrambledText = config.scrambleChars.repeat(Math.ceil(originalText.length / config.scrambleChars.length)).substring(0, originalText.length);
			gsap.set(el, {
				text: scrambledText,
				force3D: true,
			});

			// Animate to reveal real text
			gsap.to(el, {
				duration: config.duration,
				delay: config.delay,
				ease: config.ease,
				scrambleText: {
					text: originalText,
					chars: config.scrambleChars,
					speed: config.scrambleSpeed,
					revealDelay: 0,
				},
				scrollTrigger: scrollTriggerConfig,
			});
		});
	}

	/**
	 * Register custom animation
	 */
	registerAnimation(name, handler) {
		this.animations.set(name, handler);
	}

	/**
	 * Animate single element
	 */
	animateElement(el) {
		const config = this.getConfig(el);

		// Get animation handler
		const handler = this.animations.get(config.type);

		if (handler) {
			// Add initialized class
			el.classList.add('gsap-initialized');

			// Run animation
			handler(el, config);
		} else {
			console.warn(`Animation type "${config.type}" not found`);
		}
	}

	/**
	 * Initialize all animations
	 */
	init() {
		// Check for reduced motion preference
		const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

		if (prefersReducedMotion) {
			// Don't animate for users who prefer reduced motion
			console.log('Reduced motion preferred - skipping animations');
			return;
		}

		// Find and animate all elements with data-animate or data-aos
		this.refresh();

		// Refresh ScrollTrigger after images load
		window.addEventListener('load', () => {
			ScrollTrigger.refresh();
		});

		// Setup mutation observer for dynamic content
		this.observeDynamicContent();
	}

	/**
	 * Refresh animations (find new elements)
	 */
	refresh() {
		const elements = document.querySelectorAll('[data-animate]:not(.gsap-initialized), [data-aos]:not(.gsap-initialized)');
		elements.forEach(el => this.animateElement(el));
	}

	/**
	 * Observe for dynamically added content
	 */
	observeDynamicContent() {
		const observer = new MutationObserver(() => {
			this.refresh();
		});

		observer.observe(document.body, {
			childList: true,
			subtree: true,
		});
	}

	/**
	 * Utility methods
	 */
	kill(element) {
		// Kill ScrollTriggers
		ScrollTrigger.getAll().forEach(st => {
			if (st.trigger === element) st.kill();
		});

		// Clean up SplitText instance if exists
		if (element._splitInstance) {
			element._splitInstance.revert();
			delete element._splitInstance;
		}
	}

	killAll() {
		ScrollTrigger.getAll().forEach(st => st.kill());
	}

	refreshScrollTrigger() {
		ScrollTrigger.refresh();
	}
}

// Create and export instance
const scrollAnimations = new ScrollAnimations();

// Auto-initialize on DOM ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => scrollAnimations.init());
} else {
	scrollAnimations.init();
}

// Export for use in other modules
export default scrollAnimations;

// Also export the class for custom extensions
export { ScrollAnimations };
